import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { examApi } from '../../services/api';
import { toast } from 'sonner';
import {
  ShieldCheck, Check, X, Eye, Loader2, FileDown, Lock,
} from 'lucide-react';

const ApprovalQueuePage = () => {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewPaper, setPreviewPaper] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await examApi.listPapers({ status: 'submitted' });
      setPapers(data.papers || []);
    } catch (e) {
      toast.error('Failed to load approval queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    if (!window.confirm('Approve this paper? It will be released for PDF download.')) return;
    try {
      await examApi.approvePaper(id);
      toast.success('Paper approved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Please write a reason'); return; }
    try {
      await examApi.rejectPaper(rejectFor.id, rejectReason);
      toast.success('Paper rejected and sent back to creator');
      setRejectFor(null); setRejectReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejection failed');
    }
  };

  const lock = async (id) => {
    if (!window.confirm('Lock this paper permanently? It can no longer be edited.')) return;
    try {
      await examApi.lockPaper(id);
      toast.success('Paper locked');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lock failed');
    }
  };

  const preview = async (paper) => {
    try {
      const { data } = await examApi.getPaper(paper.id);
      setPreviewPaper(data);
    } catch (e) {
      toast.error('Could not load paper');
    }
  };

  return (
    <div className="space-y-6" data-testid="approval-queue-page">
      <div>
        <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" /> Approval Queue
        </h1>
        <p className="text-sm text-muted-foreground">
          Review submitted papers · Approve, Reject with reason, or Lock to finalise
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {loading ? 'Loading…' : `${papers.length} paper${papers.length === 1 ? '' : 's'} awaiting approval`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : papers.length === 0 ? (
            <p className="text-center py-10 text-muted-foreground">No papers awaiting approval.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Paper Code · Title</TableHead>
                    <TableHead>Class · Subject</TableHead>
                    <TableHead>Type · Marks</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {papers.map(p => (
                    <TableRow key={p.id} data-testid={`approval-row-${p.id}`}>
                      <TableCell>
                        <p className="font-medium">{p.title}</p>
                        <p className="text-xs text-muted-foreground font-mono">{p.paper_code}</p>
                      </TableCell>
                      <TableCell>
                        {p.class_name}<br />
                        <span className="text-xs text-muted-foreground">{p.subject_name}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{p.exam_type}</Badge>
                        <p className="text-xs text-muted-foreground mt-1">{p.total_marks} marks · {p.duration_minutes} min</p>
                      </TableCell>
                      <TableCell>
                        {p.created_by_name}<br />
                        <span className="text-xs text-muted-foreground">
                          {p.submitted_at ? new Date(p.submitted_at).toLocaleString() : '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" onClick={() => preview(p)}
                                data-testid={`preview-${p.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline"
                                onClick={() => window.open(examApi.pdfUrl(p.id), '_blank')}>
                          <FileDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive"
                                onClick={() => { setRejectFor(p); setRejectReason(''); }}
                                data-testid={`reject-${p.id}`}>
                          <X className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Approved Papers — Ready to Lock</CardTitle></CardHeader>
        <CardContent>
          <ApprovedPapersList onLock={lock} />
        </CardContent>
      </Card>

      {/* Preview dialog */}
      <Dialog open={!!previewPaper} onOpenChange={(o) => !o && setPreviewPaper(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewPaper?.title}</DialogTitle>
          </DialogHeader>
          {previewPaper && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">{previewPaper.paper_code}</Badge>
                <Badge>{previewPaper.exam_type}</Badge>
                <Badge variant="secondary">{previewPaper.total_marks} marks</Badge>
                <Badge variant="outline">{previewPaper.duration_minutes} min</Badge>
                <Badge variant="outline">Set {previewPaper.set_label}</Badge>
              </div>
              {previewPaper.general_instructions && (
                <div className="bg-muted p-3 rounded text-xs whitespace-pre-line">
                  <b>General Instructions:</b>{"\n"}{previewPaper.general_instructions}
                </div>
              )}
              {previewPaper.sections?.map((sec, i) => (
                <div key={i} className="border rounded p-3">
                  <h3 className="font-semibold">
                    {sec.name} · ({sec.questions?.length || 0} × {sec.marks_per_question} marks)
                  </h3>
                  {sec.instruction && <p className="text-xs italic text-muted-foreground">{sec.instruction}</p>}
                  <ol className="list-decimal pl-5 space-y-2 mt-2 text-sm">
                    {(sec.questions || []).map(q => (
                      <li key={q.id}>
                        {q.question_text}
                        {q.options?.length > 0 && (
                          <ul className="list-disc pl-5 text-xs text-muted-foreground mt-1">
                            {q.options.map((o, j) => <li key={j}>{o}</li>)}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Paper</DialogTitle></DialogHeader>
          <div>
            <p className="text-sm mb-2">
              Rejecting <b>{rejectFor?.title}</b> — the creator will see your reason and can edit & resubmit.
            </p>
            <Label>Reason *</Label>
            <Textarea rows={3} value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      data-testid="reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button variant="destructive" onClick={reject} data-testid="confirm-reject">
              Reject Paper
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ApprovedPapersList = ({ onLock }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    examApi.listPapers({ status: 'approved' })
      .then(r => setList(r.data.papers || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (list.length === 0) return <p className="text-sm text-muted-foreground">No approved papers waiting.</p>;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Paper</TableHead>
          <TableHead>Class · Subject</TableHead>
          <TableHead>Approved By</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.map(p => (
          <TableRow key={p.id}>
            <TableCell>
              <p className="font-medium">{p.title}</p>
              <p className="text-xs text-muted-foreground font-mono">{p.paper_code}</p>
            </TableCell>
            <TableCell>{p.class_name} · {p.subject_name}</TableCell>
            <TableCell>{p.approved_by_name || '—'}</TableCell>
            <TableCell className="text-right">
              <Button size="sm" variant="outline" onClick={() => onLock(p.id)} data-testid={`lock-${p.id}`}>
                <Lock className="h-3.5 w-3.5 mr-1" /> Lock
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ApprovalQueuePage;
